import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, AlertTriangleIcon, ShieldIcon, ArrowRightIcon } from 'lucide-react';
interface Admin {
  id: number;
  name: string;
  email: string;
}
interface TransferPrimaryAdminModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (newPrimaryAdminId: number) => Promise<void>;
  currentPrimaryAdmin: string;
  availableAdmins: Admin[];
}
export const TransferPrimaryAdminModal: React.FC<TransferPrimaryAdminModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  currentPrimaryAdmin,
  availableAdmins
}) => {
  const [selectedAdminId, setSelectedAdminId] = useState<number | null>(null);
  const [confirmationText, setConfirmationText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const selectedAdmin = availableAdmins.find(a => a.id === selectedAdminId);
  const isConfirmationValid = confirmationText.toLowerCase() === 'transfer' && selectedAdminId !== null;
  const handleConfirm = async () => {
    if (!isConfirmationValid || selectedAdminId === null) return;
    setIsSubmitting(true);
    try {
      await onConfirm(selectedAdminId);
      setSelectedAdminId(null);
      setConfirmationText('');
      onClose();
    } catch (error) {
      console.error('Error transferring primary admin role:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleClose = () => {
    if (!isSubmitting) {
      setSelectedAdminId(null);
      setConfirmationText('');
      onClose();
    }
  };
  return <AnimatePresence data-id="element-2473">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 z-50" onClick={handleClose} data-id="element-2474" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.95
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={handleClose} data-id="element-2475">
            <div className="bg-surface-elevated rounded-2xl shadow-2xl w-full max-w-md border border-custom max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-id="element-2476">
              {/* Header */}
              <div className="flex items-start justify-between p-4 sm:p-6 border-b border-custom" data-id="element-2477">
                <div className="flex items-center gap-3" data-id="element-2478">
                  <div className="w-10 h-10 rounded-full bg-[#2563EB] bg-opacity-10 flex items-center justify-center flex-shrink-0" data-id="element-2479">
                    <ShieldIcon size={20} className="text-[#2563EB]" data-id="element-2480" />
                  </div>
                  <div className="min-w-0" data-id="element-2481">
                    <h2 className="text-lg sm:text-xl font-bold text-text-primary" data-id="element-2482">
                      Transfer Primary Admin
                    </h2>
                    <p className="text-xs sm:text-sm text-text-secondary mt-0.5" data-id="element-2483">
                      This action requires careful consideration
                    </p>
                  </div>
                </div>
                <button onClick={handleClose} disabled={isSubmitting} className="p-2 rounded-lg hover:bg-surface transition-colors disabled:opacity-50 flex-shrink-0" aria-label="Close modal" data-id="element-2484">
                  <XIcon size={20} className="text-text-secondary" data-id="element-2485" />
                </button>
              </div>

              {/* Content */}
              <div className="p-4 sm:p-6 space-y-4" data-id="element-2486">
                {/* Warning */}
                <div className="bg-[#FF9D33] bg-opacity-5 border border-[#FF9D33] border-opacity-20 rounded-xl p-3" data-id="element-2487">
                  <div className="flex gap-2" data-id="element-2488">
                    <AlertTriangleIcon size={16} className="text-[#FF9D33] flex-shrink-0 mt-0.5" data-id="element-2489" />
                    <div className="text-xs leading-relaxed space-y-1" data-id="element-2490">
                      <p className="font-semibold text-text-primary" data-id="element-2491">
                        You will lose Primary Admin privileges
                      </p>
                      <p className="text-text-secondary" data-id="element-2492">
                        The selected admin will become the new Primary Admin
                        with full control. You will become a Secondary Admin.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Current Primary Admin */}
                <div className="bg-surface rounded-xl p-3 border border-custom" data-id="element-2493">
                  <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-2" data-id="element-2494">
                    Current Primary Admin
                  </p>
                  <p className="text-sm font-semibold text-text-primary" data-id="element-2495">
                    {currentPrimaryAdmin}
                  </p>
                </div>

                {/* Select New Primary Admin */}
                <div data-id="element-2496">
                  <label className="block text-sm font-semibold text-text-primary mb-2" data-id="element-2497">
                    Select new Primary Admin
                  </label>
                  <select value={selectedAdminId || ''} onChange={e => setSelectedAdminId(Number(e.target.value))} disabled={isSubmitting} className="w-full px-3 py-2 bg-surface border-2 border-custom rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-[#2563EB] text-text-primary disabled:opacity-50" data-id="element-2498">
                    <option value="" data-id="element-2499">Choose an admin...</option>
                    {availableAdmins.map(admin => <option key={admin.id} value={admin.id} data-id="element-2500">
                        {admin.name} ({admin.email})
                      </option>)}
                  </select>
                  {availableAdmins.length === 0 && <p className="text-xs text-red-600 mt-2" data-id="element-2501">
                      No other admins available. You must have at least one
                      other admin to transfer the role.
                    </p>}
                </div>

                {/* Transfer Preview */}
                {selectedAdmin && <div className="bg-[#2563EB] bg-opacity-5 border border-[#2563EB] border-opacity-20 rounded-xl p-3" data-id="element-2502">
                    <div className="flex items-center justify-between gap-2 text-xs" data-id="element-2503">
                      <div className="flex-1" data-id="element-2504">
                        <p className="text-text-tertiary mb-0.5" data-id="element-2505">From</p>
                        <p className="font-semibold text-text-primary truncate" data-id="element-2506">
                          {currentPrimaryAdmin}
                        </p>
                      </div>
                      <ArrowRightIcon size={16} className="text-[#2563EB] flex-shrink-0" data-id="element-2507" />
                      <div className="flex-1" data-id="element-2508">
                        <p className="text-text-tertiary mb-0.5" data-id="element-2509">To</p>
                        <p className="font-semibold text-text-primary truncate" data-id="element-2510">
                          {selectedAdmin.name}
                        </p>
                      </div>
                    </div>
                  </div>}

                {/* Confirmation Input */}
                <div data-id="element-2511">
                  <label htmlFor="confirmation-text" className="block text-sm font-semibold text-text-primary mb-2" data-id="element-2512">
                    Type{' '}
                    <span className="font-mono text-[#2563EB]" data-id="element-2513">transfer</span>{' '}
                    to confirm
                  </label>
                  <input id="confirmation-text" type="text" value={confirmationText} onChange={e => setConfirmationText(e.target.value)} placeholder="Type 'transfer' to confirm" disabled={isSubmitting || !selectedAdminId} className="w-full px-3 py-2 bg-surface border-2 border-custom rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-[#2563EB] text-text-primary placeholder-text-tertiary disabled:opacity-50" data-id="element-2514" />
                  {confirmationText && confirmationText.toLowerCase() !== 'transfer' && <p className="text-xs text-red-600 mt-1.5" data-id="element-2515">
                        Please type exactly: transfer
                      </p>}
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 p-4 sm:p-6 border-t border-custom bg-surface" data-id="element-2516">
                <button onClick={handleClose} disabled={isSubmitting} className="px-4 py-2 border-2 border-custom text-text-primary rounded-lg font-semibold hover:bg-surface-elevated transition-colors disabled:opacity-50" data-id="element-2517">
                  Cancel
                </button>
                <button onClick={handleConfirm} disabled={!isConfirmationValid || isSubmitting} className="px-4 py-2 bg-[#2563EB] text-white rounded-lg font-semibold hover:bg-[#1d4ed8] transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-2518">
                  {isSubmitting ? <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-2519" />
                      Transferring...
                    </> : <>
                      <ShieldIcon size={16} data-id="element-2520" />
                      Transfer Role
                    </>}
                </button>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
};